import { useState, useEffect, useMemo } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { TooltipProvider } from '@/components/ui/tooltip'
import { formatNumeric, sanitizeUrl } from '@/lib/utils/stringUtils'
import AnalyticsTabBar from '@/components/dashboard/AnalyticsTabBar'
import { LoadingState } from '@/components/shared/layout'
import { OverviewCardTitle, OverviewTabs } from '@/components/shared/overview/OverviewBreakdownCard'
import {
  AnswerChip,
  KpiStrip,
  KpiTile,
  ReportPagination,
  ReportSearch,
  ReportVerdict,
  ShareMeter,
} from '@/components/shared/analytics/AnalyticsReport'
import { AiAssistantsPanel } from '@/components/shared/analytics/AiAssistantsPanel'
import { BotTrafficCard } from '@/components/shared/analytics/BotTrafficCard'
import { useAuth } from '@/lib/contexts/AuthContext'
import { computeLLMReferrals } from '@/components/shared/analytics/llmReferrals'
import { getPeriodUniqueVisitorTotal, shareOf, stackedBarWidths } from '@/lib/calculations/breakdownTotals'
import { ChartViewToggle, ShareGauge } from '@/components/shared/charts/ShareCharts'
import { usePreviousPeriodMetrics, computePeriodDelta } from '@/hooks/usePreviousPeriodMetrics'
import { DIRECT_REFERRER_VALUES, categorizeChannel } from '@/components/shared/ChannelsComponent'

interface DashboardContext {
  propertyId?: string
  token?: string
  period?: string
  filters?: any[]
  dashboardData?: any
  isLoading?: boolean
  isAggregateMode?: boolean
  enabledProperties?: { propertyId: string }[]
}

type TabType = 'channels' | 'referrals' | 'sources' | 'campaigns' | 'mediums'

interface ChannelRow {
  name: string
  count: number
  percentage: number
}

/**
 * Channel mix bar colors: the chart ramp in order, top channel in chart-1.
 * Matches Paper "Analytics · Sources" composition bar.
 */
const MIX_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)']
const MIX_SEGMENTS = 5

type MixView = 'bar' | 'gauge'
const MIX_VIEW_OPTIONS = [
  { value: 'bar' as const, label: 'Mix bar', icon: 'mix' as const },
  { value: 'gauge' as const, label: 'Semicircle gauge', icon: 'gauge' as const },
]

/** Channel names come out of categorizeChannel title-cased; the UI is sentence case. */
const sentenceCase = (name: string) => name.charAt(0) + name.slice(1).toLowerCase()


export default function ChannelsDashboard() {
  const ctx = useOutletContext<DashboardContext>()
  const { token: authToken } = useAuth()
  const params = useParams()
  const dashboardData = ctx.dashboardData
  const isLoading = ctx.isLoading
  
  // Get the base path for tab navigation
  const getBasePath = () => {
    if (params.magicId && params.password) return `/s/${params.magicId}/${params.password}`
    if (params.workspaceId) return `/workspace/${params.workspaceId}`
    if (params.propertySlugId) return `/p/${params.propertySlugId}`
    if (params.domain) return `/d/${params.domain}`
    return ''
  }
  const basePath = getBasePath()

  const [currentTab, setCurrentTab] = useState<TabType>('channels')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [mixView, setMixView] = useState<MixView>('bar')
  const itemsPerPage = 15

  // Process channel/referral data from dashboardData
  const { channelData, referralData, sourceData, campaignData, mediumData, totalSessions } = useMemo(() => {
    if (!dashboardData?.metrics) {
      return { 
        channelData: [], 
        referralData: [], 
        sourceData: [], 
        campaignData: [], 
        mediumData: [], 
        totalSessions: 0 
      }
    }

    const channelStats: Record<string, number> = {}
    const referralStats: Record<string, number> = {}
    const sourceStats: Record<string, number> = {}
    const campaignStats: Record<string, number> = {}
    const mediumStats: Record<string, number> = {}

    // Process aggregate data for channels and referrals
    const aggregate = dashboardData.metrics.aggregate || []
    aggregate
      .filter((row: any) => row.referer_group_aggregate)
      .forEach((row: any) => {
        const count = Number(row.count) || 0

        // Categorize into channels (uses both referrer domain and utm params)
        const channel = categorizeChannel(row.referer_group, row.utm_medium, row.utm_source)
        channelStats[channel] = (channelStats[channel] || 0) + count

        // Referral data — only actual referrer entries (not UTM-medium-mapped entries).
        // UTM entries have utm_medium set; referrer entries don't.
        if (!row.utm_medium) {
          const raw = row.referer_group
          const referrer = (!raw || DIRECT_REFERRER_VALUES.has(raw.toLowerCase())) ? 'Direct' : sanitizeUrl(raw)
          referralStats[referrer] = (referralStats[referrer] || 0) + count
        }
      })

    // Process UTM data
    const utmResults = dashboardData.metrics.utmResults || []
    utmResults.forEach((data: any) => {
      const count = parseInt(data.count) || 0

      if (data.utm_source_aggregate === 'utm_source' && data.utm_source) {
        sourceStats[data.utm_source] = (sourceStats[data.utm_source] || 0) + count
      }
      if (data.utm_campaign_aggregate === 'utm_campaign' && data.utm_campaign) {
        campaignStats[data.utm_campaign] = (campaignStats[data.utm_campaign] || 0) + count
      }
      if (data.utm_medium_aggregate === 'utm_medium' && data.utm_medium) {
        mediumStats[data.utm_medium] = (mediumStats[data.utm_medium] || 0) + count
      }
    })

    // Shares are taken against the period's deduped unique-visitor total. Summing
    // the rows is not a visitor count: a visitor with UTM tags also has a referrer
    // row, and one person can arrive through several channels, so the row sum
    // overstates the period and deflates every share. Shares may therefore add up
    // to more (overlap) or less (undetected source) than 100%.
    const periodTotal = getPeriodUniqueVisitorTotal(dashboardData.metrics)
    const toArray = (stats: Record<string, number>): ChannelRow[] => {
      const denominator = periodTotal || Object.values(stats).reduce((sum, val) => sum + val, 0)
      return Object.entries(stats)
        .map(([name, count]) => ({
          name,
          count,
          percentage: shareOf(count, denominator),
        }))
        .sort((a, b) => b.count - a.count)
    }

    const channelArray = toArray(channelStats)
    const referralArray = toArray(referralStats)
    const sourceArray = toArray(sourceStats)
    const campaignArray = toArray(campaignStats)
    const mediumArray = toArray(mediumStats)

    return {
      channelData: channelArray,
      referralData: referralArray,
      sourceData: sourceArray,
      campaignData: campaignArray,
      mediumData: mediumArray,
      totalSessions: periodTotal,
    }
  }, [dashboardData])

  // Get current data based on tab
  const currentData = useMemo(() => {
    switch (currentTab) {
      case 'channels': return channelData
      case 'referrals': return referralData
      case 'sources': return sourceData
      case 'campaigns': return campaignData
      case 'mediums': return mediumData
      default: return channelData
    }
  }, [currentTab, channelData, referralData, sourceData, campaignData, mediumData])

  // Filter and paginate
  const filteredData = useMemo(() => {
    if (!searchTerm) return currentData
    return currentData.filter(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [currentData, searchTerm])

  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )
  const totalPages = Math.ceil(filteredData.length / itemsPerPage)

  // Channel mix: top channels, remainder folded into "Other" so the bar stays readable.
  // Labels carry each channel's true share of unique visitors; bar widths are scaled
  // only when overlapping channels push the shares past 100%. "Other" is a sum of
  // overlapping channels, so it gets a bar segment but no percentage.
  const { mixData, mixOverlaps } = useMemo(() => {
    const rows: { name: string; percentage: number | null; share: number }[] = channelData.map((item) => ({
      name: sentenceCase(item.name),
      percentage: item.percentage,
      share: item.percentage,
    }))
    const folded =
      rows.length <= MIX_SEGMENTS
        ? rows
        : [
            ...rows.slice(0, MIX_SEGMENTS - 1),
            {
              name: 'Other',
              percentage: null,
              share: rows.slice(MIX_SEGMENTS - 1).reduce((sum, r) => sum + r.share, 0),
            },
          ]
    const widths = stackedBarWidths(folded.map((m) => m.share))
    return {
      mixData: folded.map((m, i) => ({ ...m, width: widths[i] })),
      mixOverlaps: channelData.reduce((sum, item) => sum + item.percentage, 0) > 100.5,
    }
  }, [channelData])

  // AI assistants, from the same payload as the LLM referrals tab
  const { llmData, totalVisits: aiVisits } = useMemo(() => computeLLMReferrals(dashboardData), [dashboardData])

  // Previous equal-length window for the KPI deltas. Only period totals are
  // compared: per-channel deltas would need the channel as a filter on the
  // previous-window request, which the summary endpoint doesn't take here.
  const previousPeriod = usePreviousPeriodMetrics({
    propertyIds: ctx.isAggregateMode
      ? (ctx.enabledProperties ?? []).map((p) => p.propertyId).filter(Boolean)
      : ctx.propertyId
        ? [ctx.propertyId]
        : [],
    period: ctx.period || '7d',
    filters: ctx.filters,
    token: ctx.token,
  })
  const pageViews = typeof dashboardData?.metrics?.pageViews === 'number' ? (dashboardData.metrics.pageViews as number) : null
  const visitorsDelta = totalSessions > 0 ? computePeriodDelta(totalSessions, previousPeriod.data?.visitors) : null
  const viewsDelta = pageViews != null ? computePeriodDelta(pageViews, previousPeriod.data?.views) : null
  const hasDelta = visitorsDelta != null || viewsDelta != null
  const deltaTitle = `Compared with ${previousPeriod.phrase}`

  // Reset page when changing tabs or search
  useEffect(() => {
    setCurrentPage(1)
  }, [currentTab, searchTerm])

  if (isLoading) {
    return (
      <LoadingState
        message="Loading channels data..."
        size="lg"
        fullPage
      />
    )
  }

  const topChannel = channelData[0]
  const dimensionLabel: Record<TabType, string> = {
    channels: 'Channel',
    referrals: 'Referrer',
    sources: 'Source',
    campaigns: 'Campaign',
    mediums: 'Medium',
  }

  return (
    <TooltipProvider>
      <div className="w-full">
        {/* Analytics Tab Navigation */}
        <AnalyticsTabBar basePath={basePath} className="mb-6" />

        <div className="flex flex-col gap-4">
          <ReportVerdict
            meta={`Share of unique visitors by channel in this range${hasDelta ? `. Changes compared with ${previousPeriod.phrase}` : ''}`}
          >
            {topChannel ? (
              <>
                <span>{sentenceCase(topChannel.name)} brings</span>
                <AnswerChip>{Math.round(topChannel.percentage)}%</AnswerChip>
                <span>
                  of visitors, across {channelData.length} {channelData.length === 1 ? 'channel' : 'channels'}.
                </span>
              </>
            ) : (
              <span>No channel data in this range yet.</span>
            )}
          </ReportVerdict>

          {/* One well, raised cards (White signal v2) */}
          <div className="well flex flex-col gap-2">
            {/* KPI tiles + channel mix */}
            <Card className="gap-0 py-0">
              <KpiStrip>
                <KpiTile
                  first
                  label="Unique visitors"
                  value={totalSessions > 0 ? formatNumeric(totalSessions) : '--'}
                  delta={visitorsDelta}
                  deltaTitle={deltaTitle}
                />
                {pageViews != null && (
                  <KpiTile label="Total views" value={formatNumeric(pageViews)} delta={viewsDelta} deltaTitle={deltaTitle} />
                )}
                <KpiTile label="Channels" value={formatNumeric(channelData.length)} />
                <KpiTile label="Referring sites" value={formatNumeric(referralData.length)} />
                <KpiTile label="Campaigns" value={formatNumeric(campaignData.length)} />
                <KpiTile label="AI assistant visits" value={formatNumeric(aiVisits)} />
              </KpiStrip>

              {mixData.length > 0 && (
                <div className="flex flex-col gap-3.5 px-5 pt-4 pb-5">
                  <div className="flex items-center gap-2.5">
                    <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <OverviewCardTitle>Channel mix</OverviewCardTitle>
                      <span className="text-xs leading-4 text-muted-foreground">
                        {totalSessions > 0 ? `Share of all ${formatNumeric(totalSessions)} unique visitors by channel` : 'Share of unique visitors by channel'}
                      </span>
                    </div>
                    <ChartViewToggle options={MIX_VIEW_OPTIONS} value={mixView} onChange={setMixView} />
                  </div>
                  {mixView === 'gauge' ? (
                    // Same segments, colors and legend as the bar: arc spans are the
                    // bar widths (overflow-scaled), labels keep the true shares.
                    <ShareGauge
                      className="self-center"
                      slices={mixData.map((m) => ({ key: m.name, label: m.name, value: 0, share: null, weight: m.width }))}
                      colors={MIX_COLORS}
                      total={totalSessions}
                      totalLabel="unique visitors"
                    />
                  ) : (
                  <div className="flex h-3 shrink-0 gap-[3px]" role="img" aria-label={mixData.map((m) => (m.percentage === null ? m.name : `${m.name} ${Math.round(m.percentage)}%`)).join(', ')}>
                    {mixData.map((m, i) => (
                      <span
                        key={m.name}
                        className="h-3 min-w-[3px] rounded"
                        style={{ width: `${m.width}%`, backgroundColor: MIX_COLORS[i % MIX_COLORS.length] }}
                      />
                    ))}
                  </div>
                  )}
                  <div className={mixView === 'gauge' ? 'flex flex-wrap justify-center gap-x-6 gap-y-2' : 'flex flex-wrap gap-x-6 gap-y-2'}>
                    {mixData.map((m, i) => (
                      <span key={m.name} className="flex items-center gap-[7px]">
                        <span className="size-2 shrink-0 rounded-[2px]" style={{ backgroundColor: MIX_COLORS[i % MIX_COLORS.length] }} />
                        <span className="text-xs leading-4 text-muted-foreground">{m.name}</span>
                        {m.percentage !== null && (
                          <span className="text-xs font-medium leading-4 text-foreground tabular-nums">{Math.round(m.percentage)}%</span>
                        )}
                      </span>
                    ))}
                  </div>
                  {mixOverlaps && (
                    <p className="text-xs leading-4 text-muted-foreground">
                      Shares add up to more than 100% because a visitor can arrive through more than one channel.
                    </p>
                  )}
                </div>
              )}
            </Card>

            <div className="grid grid-cols-1 items-start gap-2 lg:grid-cols-[1.4fr_1fr]">
              {/* Channels / referrers / UTM breakdown */}
              <Card className="gap-0 py-0">
                <div className="flex flex-col gap-3 px-[18px] py-3.5 sm:flex-row sm:items-center sm:justify-between">
                  <OverviewTabs
                    ariaLabel="Source breakdown"
                    options={[
                      { value: 'channels', label: 'Channels' },
                      { value: 'referrals', label: 'Referrers' },
                      { value: 'sources', label: 'Sources' },
                      { value: 'campaigns', label: 'Campaigns' },
                      { value: 'mediums', label: 'Mediums' },
                    ]}
                    value={currentTab}
                    onChange={(v) => setCurrentTab(v as TabType)}
                  />
                  <ReportSearch
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder={`Search ${currentTab === 'referrals' ? 'referrers' : currentTab}`}
                    className="sm:w-[180px]"
                  />
                </div>

                <div className="flex h-8 shrink-0 items-center gap-3 border-y border-border/60 bg-muted/60 px-[18px] text-xs leading-4 text-muted-foreground">
                  <span className="min-w-0 flex-1">{dimensionLabel[currentTab]}</span>
                  <span className="w-[110px] shrink-0 text-right">Unique visitors</span>
                  <span className="hidden w-[110px] shrink-0 text-right sm:block">Share</span>
                </div>

                {paginatedData.length === 0 ? (
                  <div className="px-[18px] py-10 text-center text-[13px] text-muted-foreground">
                    {searchTerm ? `No ${currentTab} match your search` : `No ${currentTab} data for this range yet`}
                  </div>
                ) : (
                  <div className="flex flex-col pb-2.5">
                    {paginatedData.map((item, index) => (
                      <div
                        key={`${item.name}-${index}`}
                        className="flex h-11 shrink-0 items-center gap-3 border-b border-border/60 px-[18px] last:border-0"
                      >
                        <span className="min-w-0 flex-1 truncate text-sm font-medium leading-[18px] text-foreground" title={item.name}>
                          {currentTab === 'channels' ? sentenceCase(item.name) : item.name}
                        </span>
                        <span className="w-[110px] shrink-0 text-right text-[13px] font-medium leading-4 text-foreground tabular-nums">
                          {formatNumeric(item.count)}
                        </span>
                        <span className="hidden w-[110px] shrink-0 sm:block">
                          <ShareMeter percent={item.percentage} />
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {totalPages > 1 && (
                  <ReportPagination
                    from={(currentPage - 1) * itemsPerPage + 1}
                    to={Math.min(currentPage * itemsPerPage, filteredData.length)}
                    total={filteredData.length}
                    noun="results"
                    onPrevious={() => setCurrentPage(p => Math.max(1, p - 1))}
                    onNext={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    previousDisabled={currentPage === 1}
                    nextDisabled={currentPage === totalPages}
                  />
                )}
              </Card>

              {/* AI assistants */}
              {/* AI assistants (was the LLM Referrals tab): expands in place */}
              <AiAssistantsPanel rows={llmData} totalVisits={aiVisits} />
            </div>

            {/* Bot traffic (was the /bots tab) */}
            <BotTrafficCard
              propertyId={ctx.propertyId ?? ctx.enabledProperties?.[0]?.propertyId}
              token={ctx.token || authToken}
            />
          </div>
        </div>
      </div>
    </TooltipProvider>
  )
}
